<template>
  <!-- Sidebar container -->
  <aside
    :class="[
      'fixed top-0 left-0 z-40 h-screen flex flex-col bg-navy border-r border-navy-light transition-all duration-300',
      collapsed ? 'w-[64px]' : 'w-[260px]'
    ]"
  >
    <!-- Header logo -->
    <div class="px-4 py-5 border-b border-navy-light flex items-center gap-3">
      <div class="w-9 h-9 rounded-xl bg-offwhite flex items-center justify-center flex-shrink-0">
        <img src="/enval.png" alt="Logo" class="w-7 h-7 object-contain" onerror="this.style.display='none'" />
      </div>
      <div v-if="!collapsed" class="overflow-hidden">
        <p class="text-sm font-bold text-offwhite leading-tight truncate">Smart LabStock</p>
        <p class="text-[10px] text-offwhite/40 truncate">{{ roleLabel }}</p>
      </div>
      <!-- Toggle button -->
      <button
        @click="toggleCollapse"
        class="ml-auto text-offwhite/50 hover:text-offwhite transition-colors flex-shrink-0"
        :title="collapsed ? 'Déplier' : 'Replier'"
      >
        <i :class="collapsed ? 'pi pi-bars' : 'pi pi-times'"></i>
      </button>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto py-2 px-2">
      <template v-for="section in currentMenu" :key="section.label">
        <!-- Section label -->
        <p
          v-if="!collapsed"
          class="text-[10px] font-bold uppercase tracking-widest text-offwhite/30 px-3 py-2 mt-3"
        >
          {{ section.label }}
        </p>
        <div v-else class="mt-3 border-t border-navy-light mx-1"></div>

        <!-- Nav items -->
        <template v-for="item in section.items" :key="item.to">
          <!-- Logout button -->
          <button
            v-if="item.logout"
            @click="handleLogout"
            :class="[
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-offwhite/70 hover:bg-navy-light hover:text-offwhite transition-all cursor-pointer',
              collapsed ? 'justify-center' : ''
            ]"
            :title="collapsed ? item.label : ''"
          >
            <i :class="item.icon" class="flex-shrink-0 text-base"></i>
            <span v-if="!collapsed">{{ item.label }}</span>
          </button>

          <!-- Regular nav link -->
          <router-link
            v-else
            :to="item.to"
            :class="[
              'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer',
              isActive(item.to)
                ? 'bg-teal text-offwhite font-semibold'
                : 'text-offwhite/70 hover:bg-navy-light hover:text-offwhite',
              collapsed ? 'justify-center' : ''
            ]"
            :title="collapsed ? item.label : ''"
          >
            <i :class="item.icon" class="flex-shrink-0 text-base"></i>
            <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
            <!-- Badge -->
            <span
              v-if="!collapsed && item.badge && getBadgeCount(item.badge) > 0"
              class="ml-auto bg-red-500 text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center flex-shrink-0"
            >
              {{ getBadgeCount(item.badge) > 99 ? '99+' : getBadgeCount(item.badge) }}
            </span>
          </router-link>
        </template>
      </template>
    </nav>

    <!-- Footer user -->
    <div class="px-4 py-4 border-t border-navy-light mt-auto">
      <div class="flex items-center gap-3">
        <!-- Avatar -->
        <div class="w-9 h-9 rounded-full bg-teal text-offwhite font-bold text-sm flex items-center justify-center flex-shrink-0">
          {{ userInitials }}
        </div>
        <div v-if="!collapsed" class="overflow-hidden flex-1">
          <p class="text-sm font-semibold text-offwhite truncate">{{ userName }}</p>
          <p class="text-[10px] text-offwhite/40 truncate">{{ roleLabel }}</p>
        </div>
        <!-- Dark mode toggle -->
        <button
          @click="themeStore.toggle()"
          class="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-offwhite/50 hover:text-offwhite hover:bg-navy-light transition-colors"
          :title="themeStore.isDark ? 'Mode clair' : 'Mode sombre'"
        >
          <i :class="themeStore.isDark ? 'pi pi-sun' : 'pi pi-moon'"></i>
        </button>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useThemeStore } from '@/stores/theme.js'
import alertService from '@/services/alertService'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const themeStore = useThemeStore()

// ── Collapse state ──────────────────────────────────────────────
const collapsed = ref(window.innerWidth < 1024)

const toggleCollapse = () => {
  collapsed.value = !collapsed.value
}

const handleResize = () => {
  if (window.innerWidth < 1024) {
    collapsed.value = true
  }
}

// ── User info ───────────────────────────────────────────────────
const userName = computed(() => {
  const user = authStore.user
  return user ? (user.fullName || (user.firstName + ' ' + user.lastName)) : 'Utilisateur'
})

const userInitials = computed(() => {
  const user = authStore.user
  return user ? (user.initials || 'U') : 'U'
})

const roleLabel = computed(() => {
  const user = authStore.user
  return user ? authStore.getRoleLabel(user.role) : ''
})

const userRole = computed(() => {
  const user = authStore.user
  return user ? user.role : ''
})

// ── Badge counters ──────────────────────────────────────────────
const alertsCount = ref(0)
const demandesCount = ref(0)
let refreshInterval = null

const getBadgeCount = (badgeKey) => {
  if (badgeKey === 'alerts') return alertsCount.value
  if (badgeKey === 'demandes') return demandesCount.value
  return 0
}

const loadBadgeCounts = async () => {
  try {
    const stats = await alertService.getStatistics()
    alertsCount.value = (stats && stats.unread) ? stats.unread : 0
    demandesCount.value = (stats && stats.pendingDemandes) ? stats.pendingDemandes : 0
  } catch (error) {
    console.error('Erreur chargement badges sidebar:', error)
  }
}

// ── Active link detection ───────────────────────────────────────
const isActive = (path) => {
  return route.path === path || route.path.startsWith(path + '/')
}

// ── Logout ──────────────────────────────────────────────────────
const handleLogout = async () => {
  try {
    await authStore.logout()
  } catch (e) {
    console.error('Erreur déconnexion:', e)
  }
  router.push('/login')
}

// ── Menu configuration ──────────────────────────────────────────
const MENUS = {
  ADMIN: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/admin/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Gestion',
      items: [
        { label: 'Utilisateurs', to: '/admin/utilisateurs', icon: 'pi pi-users' },
        { label: 'Produits', to: '/admin/produits', icon: 'pi pi-box' },
        { label: 'Laboratoires', to: '/admin/laboratoires', icon: 'pi pi-home' },
        { label: 'Types de laboratoires', to: '/admin/types-laboratoires', icon: 'pi pi-tags' },
        { label: 'Unités & Conditionnements', to: '/admin/unites', icon: 'pi pi-calculator' },
        { label: 'Départements', to: '/admin/departements', icon: 'pi pi-building' }
      ]
    },
    {
      label: 'Suivi',
      items: [
        { label: 'Historique global', to: '/admin/historique', icon: 'pi pi-history' },
        { label: 'Rapports globaux', to: '/admin/rapports', icon: 'pi pi-chart-bar' },
        { label: 'Alertes', to: '/admin/alertes', icon: 'pi pi-bell', badge: 'alerts' },
        { label: 'Demandes', to: '/admin/demandes', icon: 'pi pi-inbox', badge: 'demandes' },
        { label: 'Mouvements de stock', to: '/admin/mouvements', icon: 'pi pi-arrows-v' },
        { label: 'Logs d\'audit', to: '/admin/logs', icon: 'pi pi-file-edit' }
      ]
    },
    {
      label: 'Équipements',
      items: [
        { label: 'Équipements', to: '/admin/equipements', icon: 'pi pi-box' },
        { label: 'Maintenance Équipements', to: '/admin/maintenances', icon: 'pi pi-wrench' },
        { label: 'Maintenance Infrastructure', to: '/admin/maintenances-infrastructure', icon: 'pi pi-building' }
      ]
    },
    {
      label: 'Configuration Système',
      items: [
        { label: 'Config. Analyses', to: '/admin/analysis-config', icon: 'pi pi-sliders-h' },
        { label: 'Calcul Coût Analyse', to: '/admin/calcul-cout-analyse', icon: 'pi pi-calculator' },
        { label: 'Jobs Planifiés', to: '/admin/jobs', icon: 'pi pi-cog' },
        { label: 'Configuration Alertes', to: '/admin/configuration-alertes', icon: 'pi pi-sliders-v' },
        { label: 'Paramètres', to: '/admin/parametres', icon: 'pi pi-cog' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/admin/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/admin/change-password', icon: 'pi pi-key' },
        { label: 'Déconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  RESPONSABLE_ACHAT: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/achat/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Stock Central',
      items: [
        { label: 'Stock général', to: '/achat/stock-general', icon: 'pi pi-warehouse' },
        { label: 'Dépôt YAOU', to: '/achat/stock-yaou', icon: 'pi pi-building' },
        { label: 'Vue d\'ensemble labos', to: '/achat/vue-ensemble-stocks', icon: 'pi pi-th-large' }
      ]
    },
    {
      label: 'Commandes',
      items: [
        { label: 'Commandes', to: '/achat/commandes', icon: 'pi pi-list' },
        { label: 'Produits', to: '/achat/produits', icon: 'pi pi-tags' },
        { label: 'Catégories', to: '/achat/categories', icon: 'pi pi-folder' },
        { label: 'Proformas', to: '/achat/proformas', icon: 'pi pi-file-import' },
        { label: 'Réceptions', to: '/achat/receptions', icon: 'pi pi-inbox' },
        { label: 'Dispatch', to: '/achat/dispatch', icon: 'pi pi-send' }
      ]
    },
    {
      label: 'Fournisseurs',
      items: [
        { label: 'Fournisseurs', to: '/achat/fournisseurs', icon: 'pi pi-building' }
      ]
    },
    {
      label: 'Budget',
      items: [
        { label: 'Budget', to: '/achat/budget', icon: 'pi pi-dollar' }
      ]
    },
    {
      label: 'Suivi',
      items: [
        { label: 'Alertes', to: '/achat/alertes', icon: 'pi pi-bell', badge: 'alerts' },
        { label: 'Demandes réappro.', to: '/achat/demandes', icon: 'pi pi-inbox', badge: 'demandes' },
        { label: 'Demandes services', to: '/achat/demandes-generales', icon: 'pi pi-file-edit' },
        { label: 'Mouvements de stock', to: '/achat/mouvements', icon: 'pi pi-arrows-v' },
        { label: 'Équipements', to: '/achat/equipements', icon: 'pi pi-box' },
        { label: 'Jobs Planifiés', to: '/achat/jobs', icon: 'pi pi-cog' },
        { label: 'Configuration Alertes', to: '/achat/configuration-alertes', icon: 'pi pi-sliders-v' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/achat/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/achat/change-password', icon: 'pi pi-key' },
        { label: 'Paramètres', to: '/achat/parametres', icon: 'pi pi-cog' },
        { label: 'Déconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  RESPONSABLE_LABO: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/labo/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Gestion Stock',
      items: [
        { label: 'Mes Produits', to: '/labo/produits', icon: 'pi pi-list' },
        { label: 'Inventaire', to: '/labo/stock', icon: 'pi pi-box' },
        { label: 'Sorties', to: '/labo/sorties', icon: 'pi pi-arrow-down' }
      ]
    },
    {
      label: 'Transferts',
      items: [
        { label: 'Transferts', to: '/labo/transferts', icon: 'pi pi-sync' }
      ]
    },
    {
      label: 'Approvisionnement',
      items: [
        { label: 'Demandes', to: '/labo/demandes', icon: 'pi pi-shopping-cart', badge: 'demandes' }
      ]
    },
    {
      label: 'Analyses',
      items: [
        { label: 'Équipe', to: '/labo/equipe', icon: 'pi pi-users' },
        { label: 'Échantillons', to: '/labo/echantillons', icon: 'pi pi-box' },
        { label: 'Rapports', to: '/labo/rapports', icon: 'pi pi-chart-bar' },
        { label: 'Alertes', to: '/labo/alertes', icon: 'pi pi-bell', badge: 'alerts' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/labo/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/labo/change-password', icon: 'pi pi-key' },
        { label: 'Paramètres', to: '/labo/parametres', icon: 'pi pi-cog' },
        { label: 'Déconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  // Correspondant achat — mêmes accès que RESPONSABLE_LABO
  CORRESPONDANT_ACHAT: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/labo/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Gestion Stock',
      items: [
        { label: 'Mes Produits', to: '/labo/produits', icon: 'pi pi-list' },
        { label: 'Inventaire', to: '/labo/stock', icon: 'pi pi-box' },
        { label: 'Sorties', to: '/labo/sorties', icon: 'pi pi-arrow-down' }
      ]
    },
    {
      label: 'Transferts',
      items: [
        { label: 'Transferts', to: '/labo/transferts', icon: 'pi pi-sync' }
      ]
    },
    {
      label: 'Approvisionnement',
      items: [
        { label: 'Demandes', to: '/labo/demandes', icon: 'pi pi-shopping-cart', badge: 'demandes' }
      ]
    },
    {
      label: 'Analyses',
      items: [
        { label: 'Équipe', to: '/labo/equipe', icon: 'pi pi-users' },
        { label: 'Échantillons', to: '/labo/echantillons', icon: 'pi pi-box' },
        { label: 'Rapports', to: '/labo/rapports', icon: 'pi pi-chart-bar' },
        { label: 'Alertes', to: '/labo/alertes', icon: 'pi pi-bell', badge: 'alerts' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/labo/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/labo/change-password', icon: 'pi pi-key' },
        { label: 'Paramètres', to: '/labo/parametres', icon: 'pi pi-cog' },
        { label: 'Déconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  TECHNICIEN: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/technicien/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Consultation',
      items: [
        { label: 'Stock', to: '/technicien/stock', icon: 'pi pi-box' }
      ]
    },
    {
      label: 'Saisie',
      items: [
        { label: 'Sorties', to: '/technicien/sorties', icon: 'pi pi-arrow-down' }
      ]
    },
    {
      label: 'Demandes',
      items: [
        { label: 'Mes demandes', to: '/technicien/demandes', icon: 'pi pi-shopping-cart', badge: 'demandes' }
      ]
    },
    {
      label: 'Analyses',
      items: [
        { label: 'Gestion des analyses', to: '/technicien/analyses', icon: 'pi pi-flask' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/technicien/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/technicien/change-password', icon: 'pi pi-key' },
        { label: 'Paramètres', to: '/technicien/parametres', icon: 'pi pi-cog' },
        { label: 'Déconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  CONTROLEUR_GESTION: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/controle/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Analyse & Contrôle',
      items: [
        { label: 'Analyse des stocks', to: '/controle/stocks', icon: 'pi pi-chart-bar' },
        { label: 'Contrôle budgétaire', to: '/controle/budgets', icon: 'pi pi-dollar' },
        { label: 'Tableau de bord KPI', to: '/controle/kpis', icon: 'pi pi-chart-pie' }
      ]
    },
    {
      label: 'Rapports',
      items: [
        { label: 'Rapports', to: '/controle/rapports', icon: 'pi pi-file-pdf' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/controle/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/controle/change-password', icon: 'pi pi-key' },
        { label: 'Paramètres', to: '/controle/parametres', icon: 'pi pi-cog' },
        { label: 'Déconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  // DAF — Direction Administrative et Financière
  DAF: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/daf/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Consultation',
      items: [
        { label: 'Commandes', to: '/daf/commandes', icon: 'pi pi-shopping-cart' },
        { label: 'Demandes réappro.', to: '/daf/demandes', icon: 'pi pi-inbox' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/daf/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/daf/change-password', icon: 'pi pi-key' },
        { label: 'Déconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  // Comptabilité
  COMPTABLE: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/compta/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Consultation',
      items: [
        { label: 'Commandes', to: '/compta/commandes', icon: 'pi pi-shopping-cart' },
        { label: 'Demandes', to: '/compta/demandes', icon: 'pi pi-inbox' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/compta/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/compta/change-password', icon: 'pi pi-key' },
        { label: 'Deconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  // DG — Direction Générale
  DG: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/dg/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Consultation',
      items: [
        { label: 'Commandes', to: '/dg/commandes', icon: 'pi pi-shopping-cart' },
        { label: 'Demandes', to: '/dg/demandes', icon: 'pi pi-inbox' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/dg/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/dg/change-password', icon: 'pi pi-key' },
        { label: 'Deconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ],

  // Responsable de service — département de type AUTRE
  RESPONSABLE_SERVICE: [
    {
      label: 'Tableau de bord',
      items: [
        { label: 'Tableau de bord', to: '/service/dashboard', icon: 'pi pi-chart-line' }
      ]
    },
    {
      label: 'Mes Demandes',
      items: [
        { label: 'Demandes générales', to: '/service/demandes-generales', icon: 'pi pi-file-edit', badge: 'demandes' },
        { label: 'Nouvelle demande', to: '/service/demandes-generales/nouvelle', icon: 'pi pi-plus' },
        { label: 'Réapprovisionnement', to: '/service/demandes', icon: 'pi pi-shopping-cart' }
      ]
    },
    {
      label: 'Suivi',
      items: [
        { label: 'Notifications', to: '/notifications', icon: 'pi pi-bell', badge: 'alerts' }
      ]
    },
    {
      label: 'Compte',
      items: [
        { label: 'Mon Profil', to: '/service/profile', icon: 'pi pi-user' },
        { label: 'Changer mot de passe', to: '/service/change-password', icon: 'pi pi-key' },
        { label: 'Déconnexion', icon: 'pi pi-sign-out', logout: true }
      ]
    }
  ]
}

const currentMenu = computed(() => {
  const role = userRole.value
  return MENUS[role] ? MENUS[role] : []
})

// ── Expose collapsed state to parent ───────────────────────────
defineExpose({ collapsed })

// ── Lifecycle ───────────────────────────────────────────────────
onMounted(() => {
  loadBadgeCounts()
  refreshInterval = setInterval(loadBadgeCounts, 60000)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
  window.removeEventListener('resize', handleResize)
})
</script>
